import { IconButton as DesignIconButton, Tooltip as DesignTooltip } from "@mui/material";
import { CloseOutlined as DesignClose, } from "@mui/icons-material";
// src/components/settings/TemplatesTab.tsx
//
// Templates management UI. Lists the current user's templates, lets them
// create / edit / delete / promote-to-default. Lives behind the "Templates"
// tab in Settings.
//
// v1 scope: email-channel templates only. SMS templates share the same
// underlying table (migration 007's `channel` column) but SMS UI is
// deferred to its own PR per the rollout plan.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import type { EmailTemplate, EmailTemplateType } from "../../types";
import {
  templateService,
  computeDeclaredVariables,
  type CreateTemplateInput,
  type TemplateChannel,
} from "../../lib/templateService";
import {
  renderTemplatePreview,
  extractTemplateVariables,
  isKnownTemplateVariableKey,
  TEMPLATE_CONTEXT_KEYS,
} from "../../lib/templateInterpolation";
import { buildTemplateContext } from "../../lib/buildTemplateContext";

// PKT-0031 v1.1a · channel-aware Settings → Message Templates surface
// (renamed from "Email Templates"). The DB schema (migration 007 + 010 +
// 026 + 032) supports email · sms · instagram_dm channels with a shared
// `email_templates` table (the rename to `message_templates` is a future
// packet · v1.1a keeps the table name + frontend type name for stability).
const TEMPLATE_TYPE_OPTIONS: Array<{
  value: EmailTemplateType;
  label: string;
}> = [
  { value: "listing", label: "Listing" },
  { value: "follow_up", label: "Follow-up" },
  { value: "introduction", label: "Introduction" },
  { value: "thank_you", label: "Thank you" },
  { value: "guest_card", label: "Guest card" },
  // PKT-0031 v1.1a · new types seeded by migration 032
  { value: "welcome", label: "Welcome" },
  { value: "tour", label: "Tour confirmation" },
];

const templateTypeLabel = (t: EmailTemplateType): string =>
  TEMPLATE_TYPE_OPTIONS.find((o) => o.value === t)?.label ?? t;

// Channel metadata · drives section cards + status badges + selector options.
// Honesty pass 2026-07-11: statuses must describe what ACTUALLY happens today.
interface ChannelMeta {
  value: TemplateChannel;
  label: string;
  status: "live" | "parked";
  statusLabel: string;
  statusHint: string;
  // Parked channels render collapsed: no editor, no "+ New" — the seeded
  // templates wait for their send path instead of implying one exists.
  parked?: boolean;
}

const CHANNEL_SECTIONS: ChannelMeta[] = [
  {
    value: "email",
    label: "Email",
    status: "live",
    statusLabel: "Live",
    statusHint:
      "Fires on Send Listings, welcome + follow-up flows, and guest cards.",
  },
  {
    value: "sms",
    label: "SMS",
    status: "live",
    statusLabel: "Prefills your texts",
    statusHint:
      "Insert personal messages and shared team starters from Messages. Every draft stays editable before Send.",
  },
  {
    value: "instagram_dm",
    label: "Instagram DM",
    status: "parked",
    statusLabel: "Parked",
    statusHint:
      "Bot intake templates are seeded; sends stay gated until the Meta app clears.",
    parked: true,
  },
];

const statusChipColor = (
  status: ChannelMeta["status"]
): "success" | "default" => (status === "live" ? "success" : "default");

// Sample context for preview. Hits every v1 variable so the user can verify
// interpolation without sending a real test email.
const PREVIEW_CONTEXT = buildTemplateContext(
  {
    first_name: "Jordan",
    last_name: "Reyes",
    min_budget: 1800,
    max_budget: 2400,
  },
  {
    full_name: "Alex Locator",
    email: "alex@locatenow.example",
    phone: "404-555-1212",
  },
  [{ property_name: "The Vivian" }, { property_name: "Parc on Peachtree" }],
  {
    today_date: "April 17, 2026",
    unsubscribe_link: "mailto:alex@locatenow.example?subject=Unsubscribe",
  }
);

interface DraftTemplate {
  id?: string;
  name: string;
  subject: string;
  body: string;
  template_type: EmailTemplateType;
  channel: TemplateChannel; // PKT-0031 v1.1a · was hardcoded to "email" before
  is_default: boolean;
}

const EMPTY_DRAFT: DraftTemplate = {
  name: "",
  subject: "",
  body: "",
  template_type: "listing",
  channel: "email",
  is_default: false,
};

const TemplatesTab: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // Ref to the Body field's textarea so placeholder chips insert at the caret.
  const bodyInputRef = useRef<HTMLTextAreaElement | null>(null);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [draft, setDraft] = useState<DraftTemplate>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({ open: false, message: "", severity: "success" });

  const showSnackbar = useCallback(
    (message: string, severity: "success" | "error" | "info" = "success") => {
      setSnackbar({ open: true, message, severity });
    },
    []
  );

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      // PKT-0031 v1.1a · load templates across ALL channels (was email-only)
      // · then group by channel section in render. Single Supabase query ·
      // no extra round-trips per channel.
      const data = await templateService.listTemplates();
      setTemplates(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to load templates";
      setLoadError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const openNew = () => {
    setDraft(EMPTY_DRAFT);
    setSaveError(null);
    setEditorOpen(true);
  };

  const openEdit = (template: EmailTemplate) => {
    setDraft({
      id: template.id,
      name: template.name,
      subject: template.subject,
      body: template.body,
      template_type: template.template_type,
      // PKT-0031 v1.1a · propagate channel from existing row · falls back
      // to "email" for legacy rows that pre-date the channel column.
      channel: ((template as any).channel as TemplateChannel) || "email",
      is_default: template.is_default,
    });
    setSaveError(null);
    setEditorOpen(true);
  };

  // PKT-0031 v1.1a · opens the new-template dialog pre-selecting a channel
  // so the "+ New" button per section creates in that channel directly.
  const openNewForChannel = (channel: TemplateChannel) => {
    setDraft({ ...EMPTY_DRAFT, channel });
    setSaveError(null);
    setEditorOpen(true);
  };

  const closeEditor = () => {
    if (saving) return;
    setEditorOpen(false);
  };

  const handleSave = async () => {
    // PKT-0031 v1.1a · subject is required only for email channel · SMS +
    // IG DM templates have no subject by design (mig 010 + 026 seed them
    // with empty subject) · validate accordingly.
    const subjectRequired = draft.channel === "email";
    if (
      !draft.name.trim() ||
      (subjectRequired && !draft.subject.trim()) ||
      !draft.body.trim()
    ) {
      setSaveError(
        subjectRequired
          ? "Name, subject, and body are all required."
          : "Name and body are required."
      );
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const payload: CreateTemplateInput = {
        name: draft.name.trim(),
        // PKT-0031 v1.1a · force empty subject for non-email channels even if
        // operator switched channel mid-edit (the field is disabled in UI so
        // it can't be edited but the stale value could leak through · normalize
        // here at save · matches mig 010 + 026 + 032 seed pattern of '' subject
        // for sms + instagram_dm rows).
        subject: draft.channel === "email" ? draft.subject : "",
        body: draft.body,
        template_type: draft.template_type,
        // PKT-0031 v1.1a · channel comes from draft (was hardcoded "email")
        channel: draft.channel,
        is_default: draft.is_default,
      };
      if (draft.id) {
        await templateService.updateTemplate(draft.id, payload);
        showSnackbar("Template updated.");
      } else {
        await templateService.createTemplate(payload);
        showSnackbar("Template created.");
      }
      setEditorOpen(false);
      await loadTemplates();
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : "Failed to save template."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTargetId) return;
    setDeleting(true);
    try {
      await templateService.deleteTemplate(deleteTargetId);
      setDeleteTargetId(null);
      showSnackbar("Template deleted.");
      await loadTemplates();
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : "Failed to delete template.",
        "error"
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleSetDefault = async (template: EmailTemplate) => {
    try {
      await templateService.setDefault(template.id);
      showSnackbar(`"${template.name}" set as default.`);
      await loadTemplates();
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : "Failed to update default.",
        "error"
      );
    }
  };

  // Insert a placeholder token at the caret in the Body field (appends when
  // the field isn't focused). The chips replace prose documentation — tap to
  // use instead of read-and-retype.
  const insertPlaceholder = (key: string) => {
    const token = `{{${key}}}`;
    const el = bodyInputRef.current;
    setDraft((d) => {
      if (el && typeof el.selectionStart === "number") {
        const start = el.selectionStart;
        const end = el.selectionEnd ?? start;
        const next =
          d.body.slice(0, start) + token + d.body.slice(end);
        // Restore the caret after React re-renders the controlled field.
        requestAnimationFrame(() => {
          el.focus();
          const caret = start + token.length;
          el.setSelectionRange(caret, caret);
        });
        return { ...d, body: next };
      }
      return { ...d, body: d.body ? `${d.body} ${token}` : token };
    });
  };

  // --- Editor-pane derived state ------------------------------------------
  const preview = useMemo(
    () => renderTemplatePreview(draft.subject, draft.body, PREVIEW_CONTEXT),
    [draft.subject, draft.body]
  );

  const declaredVariables = useMemo(
    () => computeDeclaredVariables(draft.subject, draft.body),
    [draft.subject, draft.body]
  );

  const unknownTokens = useMemo(() => {
    const all = [
      ...extractTemplateVariables(draft.subject),
      ...extractTemplateVariables(draft.body),
    ];
    return Array.from(new Set(all.filter((k) => !isKnownTemplateVariableKey(k))));
  }, [draft.subject, draft.body]);

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Button
          variant="contained"
          size={isMobile ? "small" : "medium"}
          startIcon={<AddIcon />}
          onClick={openNew}
          disabled={loading || Boolean(loadError)}
          sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
        >
          New template
        </Button>
      </Stack>

      {/* One-line orientation — placeholder education lives in the editor
          as tappable chips, not prose. */}

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Could not load templates.
          </Typography>
          <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
            {loadError}
          </Typography>
          <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
            If this is a fresh install, make sure migration 007 has been run
            against Supabase.
          </Typography>
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && !loadError && templates.length === 0 && (
        <Card variant="outlined" sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            No templates yet.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create one to replace the default Send / Text / DM copy with
            something reusable.
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={openNew}>
            Create your first template
          </Button>
        </Card>
      )}

      {/* Channel cards (2026-07-11 overhaul): one card per channel, built for
          the phone first. Row 1 = channel + honest status + New; row 2 = the
          truth about what actually sends, full-width (never a squeezed side
          column). Templates render as tappable rows inside the card. Parked
          channels collapse — no editor, no "+ New". */}
      {!loading && !loadError && templates.length > 0 && (
        <Stack spacing={2}>
          {CHANNEL_SECTIONS.map((section) => {
            const sectionTemplates = templates.filter(
              (t) => ((t as any).channel as TemplateChannel) === section.value
            );

            if (section.parked) {
              return (
                <Card key={section.value} variant="outlined" sx={{ opacity: 0.75 }}>
                  <CardContent sx={{ "&:last-child": { pb: 2 } }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                        {section.label}
                      </Typography>
                      <Chip
                        size="small"
                        label={section.statusLabel}
                        variant="outlined"
                        sx={{ height: 20, fontSize: 11 }}
                      />
                      <Box sx={{ flex: 1 }} />
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {sectionTemplates.length} seeded
                      </Typography>
                    </Stack>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      {section.statusHint}
                    </Typography>
                  </CardContent>
                </Card>
              );
            }

            return (
              <Card key={section.value} variant="outlined">
                <CardContent sx={{ pb: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                      {section.label}
                    </Typography>
                    <Chip
                      size="small"
                      label={section.statusLabel}
                      color={statusChipColor(section.status)}
                      sx={{ height: 20, fontSize: 11 }}
                    />
                    <Box sx={{ flex: 1 }} />
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => openNewForChannel(section.value)}
                      sx={{ flexShrink: 0 }}
                    >
                      New
                    </Button>
                  </Stack>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.5, mb: 1 }}
                  >
                    {section.statusHint}
                  </Typography>

                  {sectionTemplates.length === 0 ? (
                    <Box
                      sx={{
                        p: 2,
                        textAlign: "center",
                        borderRadius: 2,
                        bgcolor: "action.hover",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No {section.label} templates yet.
                      </Typography>
                    </Box>
                  ) : (
                    <Stack divider={<Divider flexItem />} spacing={0}>
                      {sectionTemplates.map((template) => (
                        <Stack
                          key={template.id}
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          onClick={() => openEdit(template)}
                          sx={{
                            py: 1.25,
                            px: 0.5,
                            cursor: "pointer",
                            borderRadius: 2,
                            "&:hover": {
                              bgcolor: alpha(theme.palette.primary.main, 0.05),
                            },
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 650 }}
                                noWrap
                              >
                                {template.name}
                              </Typography>
                              {template.is_default && (
                                <StarIcon
                                  sx={{ fontSize: 15, color: "warning.main" }}
                                />
                              )}
                            </Stack>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                              sx={{ display: "block" }}
                            >
                              {templateTypeLabel(template.template_type)}
                              {section.value === "email" && template.subject
                                ? ` · ${template.subject}`
                                : ""}
                            </Typography>
                          </Box>
                          <Stack
                            direction="row"
                            spacing={0}
                            sx={{ flexShrink: 0 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {!template.is_default && (
                              <Tooltip title="Set as default for its category">
                                <IconButton
                                  size="small"
                                  aria-label="Set as default"
                                  onClick={() => handleSetDefault(template)}
                                >
                                  <StarBorderIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                aria-label="Edit template"
                                onClick={() => openEdit(template)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                aria-label="Delete template"
                                onClick={() => setDeleteTargetId(template.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      {/* Editor Dialog — full-screen sheet on mobile ----------------------- */}
      <Dialog
        open={editorOpen}
        onClose={closeEditor}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ pr: 7, position: "relative" }}>
          {draft.id ? "Edit Template" : "New Template"}<span style={{ position: "absolute", right: 8, top: 8 }}><DesignTooltip title="Close"><span><DesignIconButton aria-label="Close" onClick={closeEditor}  sx={{ width: 40, height: 40 }}><DesignClose sx={{ fontSize: 20 }} /></DesignIconButton></span></DesignTooltip></span>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Template name"
                value={draft.name}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, name: e.target.value }))
                }
                fullWidth
                required
                autoFocus
                placeholder="e.g. Standard listing send"
              />
              {/* PKT-0031 v1.1a · Channel selector. Editing an existing template
                  preserves its channel · creating a new one defaults to whichever
                  section's "+ New" button was clicked (or "email" via openNew). */}
              <FormControl sx={{ minWidth: 160 }}>
                <InputLabel>Channel</InputLabel>
                <Select
                  value={draft.channel}
                  label="Channel"
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      channel: e.target.value as TemplateChannel,
                    }))
                  }
                >
                  {/* Parked channels aren't creatable — only offered when
                      editing an existing row already on that channel. */}
                  {CHANNEL_SECTIONS.filter(
                    (opt) => !opt.parked || draft.channel === opt.value
                  ).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={draft.template_type}
                  label="Type"
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      template_type: e.target.value as EmailTemplateType,
                    }))
                  }
                >
                  {TEMPLATE_TYPE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {/* PKT-0031 v1.1a · Subject only applies to email channel · SMS +
                IG DM don't carry a subject (they're body-only). Disable + hint
                for non-email so the operator sees why the field is greyed out
                rather than hiding it (discoverability over minimalism). */}
            <TextField
              label={draft.channel === "email" ? "Subject" : "Subject (not used for this channel)"}
              value={draft.channel === "email" ? draft.subject : ""}
              onChange={(e) =>
                setDraft((d) => ({ ...d, subject: e.target.value }))
              }
              fullWidth
              required={draft.channel === "email"}
              disabled={draft.channel !== "email"}
              placeholder={
                draft.channel === "email"
                  ? "e.g. Apartment Options for {{client_first_name}}"
                  : "SMS and Instagram DM templates have no subject"
              }
              helperText={
                draft.channel !== "email"
                  ? `${draft.channel === "sms" ? "SMS" : "Instagram DM"} carries body-only · subject field stays empty`
                  : undefined
              }
            />

            <TextField
              label="Body"
              value={draft.body}
              onChange={(e) =>
                setDraft((d) => ({ ...d, body: e.target.value }))
              }
              inputRef={bodyInputRef}
              fullWidth
              required
              multiline
              minRows={isMobile ? 5 : 6}
              maxRows={16}
              placeholder={
                "Hi {{client_first_name}},\n\nI found {{property_count}} places that match what you're looking for..."
              }
            />

            {/* Placeholder chips — tap to insert at the caret. Replaces the
                old prose documentation of the {{...}} system. */}
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                Tap to insert a placeholder — it fills per client at send time
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.75 }}>
                {TEMPLATE_CONTEXT_KEYS.map((key) => (
                  <Chip
                    key={key}
                    size="small"
                    label={key}
                    onClick={() => insertPlaceholder(key)}
                    sx={{ fontFamily: "monospace", fontSize: 11, cursor: "pointer" }}
                  />
                ))}
              </Stack>
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={draft.is_default}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, is_default: e.target.checked }))
                  }
                />
              }
              label="Use as default template for this type"
            />

            {/* Variable summary ------------------------------------------- */}
            <Box sx={{ borderRadius: 1, bgcolor: "action.hover", p: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Detected variables
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                {declaredVariables.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    None yet. Try adding{" "}
                    <code>{`{{client_first_name}}`}</code>.
                  </Typography>
                )}
                {declaredVariables.map((v) => (
                  <Chip
                    key={v}
                    size="small"
                    label={v}
                    sx={{ fontFamily: "monospace" }}
                  />
                ))}
              </Stack>
              {unknownTokens.length > 0 && (
                <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
                  <Typography variant="caption">
                    Unsupported placeholder{unknownTokens.length > 1 ? "s" : ""}{" "}
                    will render as empty:{" "}
                    {unknownTokens
                      .map((t) => `{{${t}}}`)
                      .join(", ")}
                  </Typography>
                </Alert>
              )}
            </Box>

            {/* Preview ---------------------------------------------------- */}
            <Box
              sx={{
                borderRadius: 1,
                border: 1,
                borderColor: "divider",
                p: 1.5,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 1 }}
              >
                <VisibilityIcon fontSize="small" color="action" />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Preview (sample data)
                </Typography>
              </Stack>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, mb: 0.5 }}
              >
                {preview.subject || (
                  <em style={{ color: "#999" }}>(no subject)</em>
                )}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ whiteSpace: "pre-wrap" }}
              >
                {preview.body || (
                  <em style={{ color: "#999" }}>(no body)</em>
                )}
              </Typography>
            </Box>

            {saveError && <Alert severity="error">{saveError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditor} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : undefined}
          >
            {draft.id ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation --------------------------------------------- */}
      <Dialog
        open={deleteTargetId !== null}
        onClose={() => !deleting && setDeleteTargetId(null)}
      >
        <DialogTitle sx={{ pr: 7, position: "relative" }}>Delete this template?<span style={{ position: "absolute", right: 8, top: 8 }}><DesignTooltip title="Close"><span><DesignIconButton aria-label="Close" onClick={() => setDeleteTargetId(null)} disabled={deleting}  sx={{ width: 40, height: 40 }}><DesignClose sx={{ fontSize: 20 }} /></DesignIconButton></span></DesignTooltip></span></DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This cannot be undone. If this was your default, Send Listings
            will fall back to the built-in copy until you set a new default.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteTargetId(null)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : undefined}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TemplatesTab;
